// src/pages/ApplicationChampionshipStageStandings/components/StandingsTable/StandingsRow.tsx
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Box, Td, useToken } from '@chakra-ui/react';
import { Reorder, useDragControls } from 'framer-motion';
import { Standing } from '@/model';
import { calcGoalDiff, calcPlayed, calcWinRate } from '../../utils/calculateStandingDerived';
import { QualifierZone, ZONE_BORDER_COLOR } from '../../utils/getQualifierZone';
import { StandingEditableFields, StandingRowMode } from '../../types';
import { DragHandle } from './DragHandle';
import { TeamCell } from './TeamCell';
import { EditableNumberCell } from './EditableNumberCell';
import { ComputedCell } from './ComputedCell';
import { RowActions } from './RowActions';

type StandingsRowProps = {
  standing: Standing;
  mode: StandingRowMode;
  isDeleting?: boolean;
  isDragDisabled?: boolean;
  zone?: QualifierZone;
  onEnterEdit: () => void;
  onCancel: () => void;
  onSave: (changes: StandingEditableFields) => void;
  onDelete: () => void;
};

/** Snapshot dos campos editáveis a partir do standing remoto. */
const snapshotOf = (s: Standing): StandingEditableFields => ({
  points: s.points ?? 0,
  wins: s.wins ?? 0,
  draws: s.draws ?? 0,
  losses: s.losses ?? 0,
  goals_for: s.goals_for ?? 0,
  goals_against: s.goals_against ?? 0,
});

const hasDiff = (a: StandingEditableFields, b: StandingEditableFields): boolean =>
  a.points !== b.points ||
  a.wins !== b.wins ||
  a.draws !== b.draws ||
  a.losses !== b.losses ||
  a.goals_for !== b.goals_for ||
  a.goals_against !== b.goals_against;

export const StandingsRow: FunctionComponent<StandingsRowProps> = ({
  standing,
  mode,
  isDeleting = false,
  isDragDisabled = false,
  zone = null,
  onEnterEdit,
  onCancel,
  onSave,
  onDelete,
}) => {
  const isEditing = mode === 'edit';
  const baseline = useMemo(() => snapshotOf(standing), [standing]);
  const [draft, setDraft] = useState<StandingEditableFields>(baseline);

  // Resincroniza o draft quando o standing remoto mudar OU ao sair de edit
  useEffect(() => {
    if (mode === 'view') setDraft(baseline);
  }, [baseline, mode]);

  const current = isEditing ? draft : baseline;
  const played = calcPlayed(current.wins, current.draws, current.losses);
  const goalDiff = calcGoalDiff(current.goals_for, current.goals_against);
  const winRate = calcWinRate(current.points, played);

  const changed = isEditing && hasDiff(draft, baseline);

  const setField = (key: keyof StandingEditableFields) => (value: number) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => onSave(draft);

  const canDrag = !isEditing && mode !== 'saving' && !isDragDisabled;

  const dragControls = useDragControls();

  // Resolve tokens Chakra para style inline (Reorder.Item não aceita props Chakra)
  const [bgEdit, bgView, borderColor, zoneGreen, zoneBlue, zoneOrange, zoneRed] = useToken('colors', [
    'gray.50',
    'white',
    'gray.100',
    ZONE_BORDER_COLOR.direct,
    ZONE_BORDER_COLOR.playoff,
    ZONE_BORDER_COLOR.secondary,
    ZONE_BORDER_COLOR.relegation,
  ]);

  const zoneBorderColor =
    zone === 'direct'
      ? zoneGreen
      : zone === 'playoff'
      ? zoneBlue
      : zone === 'secondary'
      ? zoneOrange
      : zone === 'relegation'
      ? zoneRed
      : 'transparent';

  return (
    <Reorder.Item
      as="tr"
      value={standing.uuid}
      dragListener={false}
      dragControls={dragControls}
      style={{
        background: isEditing ? bgEdit : bgView,
        borderTop: `1px solid ${borderColor}`,
        borderLeft: `4px solid ${zoneBorderColor}`,
        position: 'relative',
        touchAction: 'none',
      }}
    >
      <Td px={2} w="32px">
        <DragHandle
          isDisabled={!canDrag}
          onPointerDown={(event) => dragControls.start(event)}
          aria-label="drag"
        />
      </Td>

      <Td px={2} w="48px" textAlign="center">
        <Box
          w="28px"
          h="28px"
          borderRadius="full"
          bg="gray.100"
          color="gray.700"
          fontSize="xs"
          fontWeight="bold"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
        >
          {standing.position}
        </Box>
      </Td>

      <Td px={3} minW="180px">
        <TeamCell
          name={standing.team?.name ?? '—'}
          acronym={standing.team?.acronym}
          logoPath={standing.team?.logo_path}
        />
      </Td>

      <Td px={2} w="72px">
        <EditableNumberCell
          value={current.points}
          isEditing={isEditing}
          onChange={setField('points')}
          ariaLabel="points"
        />
      </Td>

      <Td px={2} w="56px">
        <ComputedCell value={played} />
      </Td>

      <Td px={2} w="56px">
        <EditableNumberCell
          value={current.wins}
          isEditing={isEditing}
          onChange={setField('wins')}
          ariaLabel="wins"
        />
      </Td>

      <Td px={2} w="56px">
        <EditableNumberCell
          value={current.draws}
          isEditing={isEditing}
          onChange={setField('draws')}
          ariaLabel="draws"
        />
      </Td>

      <Td px={2} w="56px">
        <EditableNumberCell
          value={current.losses}
          isEditing={isEditing}
          onChange={setField('losses')}
          ariaLabel="losses"
        />
      </Td>

      <Td px={2} w="64px">
        <EditableNumberCell
          value={current.goals_for}
          isEditing={isEditing}
          onChange={setField('goals_for')}
          ariaLabel="goals_for"
        />
      </Td>

      <Td px={2} w="64px">
        <EditableNumberCell
          value={current.goals_against}
          isEditing={isEditing}
          onChange={setField('goals_against')}
          ariaLabel="goals_against"
        />
      </Td>

      <Td px={2} w="64px">
        <ComputedCell value={goalDiff} format="signed" />
      </Td>

      <Td px={2} w="88px">
        <ComputedCell value={winRate} format="percent" />
      </Td>

      <Td px={2} w="88px" textAlign="center">
        <RowActions
          mode={mode}
          hasChanges={changed}
          onEdit={onEnterEdit}
          onCancel={() => {
            setDraft(baseline);
            onCancel();
          }}
          onSave={handleSave}
          onDelete={onDelete}
          isDeleting={isDeleting}
        />
      </Td>
    </Reorder.Item>
  );
};

export default StandingsRow;
